import { gql } from '@apollo/client';
import { ItemSortOrder } from './item-context-types';
import {
  Order_By,
  Variants_Packs_Order_By,
  Visible_Variant_Packs_Order_By,
} from '@/types';
import { SALT_BASE_ITEM_FIELDS_FRAGMENT } from './item-context-fragments';
import { useMemo } from 'react';
import { useUserContext, useUserSettingsContext } from './user-context';

export const PACK_FRAGMENT = gql`
  fragment ItemsContext_PackFields on packs {
    base_unit
    max_unit_size
    min_unit_size
    unit
    unit_count
    uuid
  }
`;

export const ITEM_MODIFIERS_FRAGMENT = gql`
  fragment ItemsContext_ItemModifiersFields on variants_packs_modifier_options {
    modifier_option {
      modifier_option_group {
        name
        sort_order
        uuid
      }
      name
      sort_order
      uuid
    }
  }
`;

export const VARIANTS_PACK_GROUP_ITEMS_FRAGMENT = gql`
  fragment ItemsContext_VariantsPackGroupItemsFields on variant_pack_groups {
    display_image_url
    type
    uuid
  }
`;

export const VARIANTS_PACK_ITEM_GROUPS_MEMBERSHIP_FRAGMENT = gql`
  fragment ItemsContext_VariantsPackItemGroupsMembershipFields on variants_packs {
    variant_pack_group_memberships @skip(if: $skipGroupMemberships) {
      variant_pack_group_uuid
      uuid
    }
  }
`;

export const VARIANTS_PACK_FRAGMENT = gql`
  fragment ItemsContext_VariantPackSaltCacheFields on variant_pack_salt_cache {
    description_translations
    image_urls
    name_translations
  }

  fragment ItemsContext_VariantsPackFields on variants_packs {
    ...ItemsContext_VariantsPackItemGroupsMembershipFields
    external_item_id
    gtin_metadata @include(if: $skipSaltApi) {
      gtin
      metadata
      uuid
    }
    item {
      category
      description
      display_name
      extra_keywords
      photo_url_list
      restaurant_item_overrides(
        where: { restaurant_uuid: { _eq: $restaurantUUID } }
      ) {
        display_name
        category
        uuid
      }
      uuid
    }
    lead_time_cutoff_time
    lead_time_days
    metadata
    modifier_options {
      ...ItemsContext_ItemModifiersFields
    }
    pack {
      ...ItemsContext_PackFields
    }
    prebook_config
    salt(locale: $locale, restaurantId: $restaurantUUID)
      @skip(if: $skipLiveSalt) {
      ...ItemContext_SaltBaseItemFields
    }
    salt_cache @include(if: $useSaltCache) {
      ...ItemsContext_VariantPackSaltCacheFields
    }
    upc_list
    uuid
  }

  ${VARIANTS_PACK_ITEM_GROUPS_MEMBERSHIP_FRAGMENT}
  ${ITEM_MODIFIERS_FRAGMENT}
  ${SALT_BASE_ITEM_FIELDS_FRAGMENT}
  ${PACK_FRAGMENT}
`;

export const ORDER_GUIDE_ITEM_FRAGMENT = gql`
  fragment ItemsContext_OrderGuideItemFields on OrderGuideItemEntryResponse {
    order_guide_item_id
    order_guide_item_partition_id
    order_guide_restaurant {
      is_minimized
      memo
      notes
      order_guide_restaurant_partition_uuid
      par_level
      persist_memo
      persist_notes
      persist_request
      request
      sort_order
      uuid
    }
    variant_pack {
      ...ItemsContext_VariantsPackFields
      ...ItemsContext_VariantsPackItemGroupsMembershipFields
    }
  }

  ${VARIANTS_PACK_FRAGMENT}
  ${VARIANTS_PACK_ITEM_GROUPS_MEMBERSHIP_FRAGMENT}
`;

export const ORDER_GUIDE_RESTAURANT_PARTITION_FRAGMENT = gql`
  fragment ItemsContext_OrderGuideRestaurantPartitionFields on order_guide_restaurant_partition {
    is_default
    name
    sort_order
    uuid
  }
`;

export const ORDER_GUIDE_RESTAURANT_COLLECTION_FRAGMENT = gql`
  fragment ItemsContext_OrderGuideRestaurantCollectionFields on order_guide_restaurant_collection {
    is_default
    linked_order_guide_supplier_collection_uuid
    name
    order_guide_restaurant_partitions(
      order_by: [{ sort_order: asc_nulls_last }, { name: asc_nulls_last }]
    ) {
      ...ItemsContext_OrderGuideRestaurantPartitionFields
    }
    sort_order
    uuid
  }

  ${ORDER_GUIDE_RESTAURANT_PARTITION_FRAGMENT}
`;

export const ORDER_GUIDE_SUPPLIER_PARTITION_FRAGMENT = gql`
  fragment ItemsContext_OrderGuideSupplierPartitionFields on order_guide_supplier_partition {
    is_default
    name
    sort_order
    uuid
  }
`;

export const ORDER_GUIDE_SUPPLIER_COLLECTION_FRAGMENT = gql`
  fragment ItemsContext_OrderGuideSupplierCollectionFields on order_guide_supplier_collection {
    is_default
    name
    order_guide_supplier_partitions(
      order_by: [{ sort_order: asc_nulls_last }, { name: asc_nulls_last }]
    ) {
      ...ItemsContext_OrderGuideSupplierPartitionFields
    }
    sort_order
    uuid
  }

  ${ORDER_GUIDE_SUPPLIER_PARTITION_FRAGMENT}
`;

export const MULTI_ORDER_GUIDE_QUERY = gql`
  query ItemsContext_MultiOrderGuideItems(
    $locale: String!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
  ) {
    getOrderGuideItems(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      ...ItemsContext_OrderGuideItemFields
    }

    order_guide_restaurant_collection(
      where: {
        restaurant_uuid: { _eq: $restaurantUUID }
        supplier_uuid: { _eq: $supplierUUID }
      }
    ) {
      ...ItemsContext_OrderGuideRestaurantCollectionFields
    }

    order_guide_supplier_collection(
      where: {
        restaurant_uuid: { _eq: $restaurantUUID }
        supplier_uuid: { _eq: $supplierUUID }
      }
    ) {
      ...ItemsContext_OrderGuideSupplierCollectionFields
    }
  }

  ${ORDER_GUIDE_ITEM_FRAGMENT}
  ${ORDER_GUIDE_RESTAURANT_COLLECTION_FRAGMENT}
  ${ORDER_GUIDE_SUPPLIER_COLLECTION_FRAGMENT}
`;

export const EDIT_MULTI_ORDER_GUIDE_QUERY = gql`
  query ItemsContext_EditMultiOrderGuideItems(
    $collectionUUID: uuid!
    $locale: String!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
  ) {
    getOrderGuideItems(
      collection_id: $collectionUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      ...ItemsContext_OrderGuideItemFields
    }

    order_guide_restaurant_collection(
      where: {
        restaurant_uuid: { _eq: $restaurantUUID }
        supplier_uuid: { _eq: $supplierUUID }
      }
    ) {
      ...ItemsContext_OrderGuideRestaurantCollectionFields
    }

    order_guide_supplier_collection(
      where: {
        restaurant_uuid: { _eq: $restaurantUUID }
        supplier_uuid: { _eq: $supplierUUID }
      }
    ) {
      ...ItemsContext_OrderGuideSupplierCollectionFields
    }
  }

  ${ORDER_GUIDE_ITEM_FRAGMENT}
  ${ORDER_GUIDE_RESTAURANT_COLLECTION_FRAGMENT}
  ${ORDER_GUIDE_SUPPLIER_COLLECTION_FRAGMENT}
`;

export const useCatalogVariantPacksQueryVariables = (supplierUUID: string) => {
  const userContext = useUserContext();
  const { catalogSortOrder } = useUserSettingsContext();

  const restaurantUUID = userContext.selectedRestaurantUUID;

  return useMemo(() => {
    let sortRules: Variants_Packs_Order_By[];

    switch (catalogSortOrder) {
      case ItemSortOrder.AlphabeticalReverse:
        sortRules = [
          { item: { display_name: Order_By.Desc } },
          { pack: { unit: Order_By.Desc } },
          { external_item_id: Order_By.DescNullsLast },
        ];
        break;
      case ItemSortOrder.OrderFrequency:
        sortRules = [
          {
            item: { order_count_l30_days: { count: Order_By.DescNullsLast } },
          },
          { item: { display_name: Order_By.Asc } },
          { pack: { unit: Order_By.Asc } },
          { external_item_id: Order_By.AscNullsLast },
        ];
        break;
      case ItemSortOrder.OrderFrequencyReverse:
        sortRules = [
          {
            item: { order_count_l30_days: { count: Order_By.Asc } },
          },
          { item: { display_name: Order_By.Asc } },
          { pack: { unit: Order_By.Asc } },
          { external_item_id: Order_By.AscNullsLast },
        ];
        break;
      case ItemSortOrder.ItemCode:
        sortRules = [
          { external_item_id: Order_By.AscNullsLast },
          { item: { display_name: Order_By.Asc } },
          { pack: { unit: Order_By.Asc } },
        ];
        break;
      case ItemSortOrder.ItemCodeReverse:
        sortRules = [
          { external_item_id: Order_By.DescNullsLast },
          { item: { display_name: Order_By.Desc } },
          { pack: { unit: Order_By.Desc } },
        ];
        break;
      default:
        sortRules = [
          { item: { display_name: Order_By.Asc } },
          { pack: { unit: Order_By.Asc } },
          { external_item_id: Order_By.AscNullsLast },
        ];
    }

    const visibleVariantPacksOrderBy: Visible_Variant_Packs_Order_By[] =
      sortRules.map((rule) => ({
        variant_pack: rule,
      }));

    return {
      restaurantUUID,
      supplierUUID,
      visibleVariantPacksOrderBy,
    };
  }, [catalogSortOrder, restaurantUUID, supplierUUID]);
};
